/**
 * Pure form rules for the self-service signup page (#3017).
 *
 * Mirrors what `POST /api/v1/signup` checks so a typo is caught before the
 * round trip. The server stays the authority: reserved and taken addresses,
 * and whether the company exists, are only knowable there.
 */

export const SLUG_MAX_LENGTH = 56;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+0-9()[\] ./-]*$/;
const BUSINESS_ID_PATTERN = /^(\d{7})-(\d)$/;
const SLUG_SHAPE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const CHECKSUM_WEIGHTS = [7, 9, 10, 5, 8, 4, 2] as const;

export type SignupFieldName =
  | 'contactName'
  | 'company'
  | 'email'
  | 'phone'
  | 'businessId'
  | 'requestedSlug'
  | 'consent';

export interface SignupFormValues {
  contactName: string;
  company: string;
  email: string;
  phone: string;
  businessId: string;
  requestedSlug: string;
  consent: boolean;
}

/** Field name → i18n key (in the `signup` namespace) of the message to show. */
export type SignupFieldErrors = Partial<Record<SignupFieldName, string>>;

/** Field order as rendered; the first invalid one receives focus. */
export const SIGNUP_FIELD_ORDER: readonly SignupFieldName[] = [
  'contactName',
  'company',
  'email',
  'phone',
  'businessId',
  'requestedSlug',
  'consent',
];

/** Y-tunnus: `NNNNNNN-N` with the weighted mod-11 check digit. */
export function isValidBusinessId(value: string): boolean {
  const match = BUSINESS_ID_PATTERN.exec(value.trim());
  const serial = match?.[1];
  const check = match?.[2];
  if (serial === undefined || check === undefined) return false;
  let sum = 0;
  for (let i = 0; i < CHECKSUM_WEIGHTS.length; i += 1) {
    sum += Number(serial[i]) * (CHECKSUM_WEIGHTS[i] ?? 0);
  }
  const remainder = sum % 11;
  if (remainder === 1) return false;
  const expected = remainder === 0 ? 0 : 11 - remainder;
  return expected === Number(check);
}

export function isValidSlug(value: string): boolean {
  return value.length <= SLUG_MAX_LENGTH && SLUG_SHAPE.test(value);
}

/** A routable address proposal from the company name (ä→a, ö→o, å→a, …). */
export function suggestSlug(company: string): string {
  return company
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/, '');
}

export function validateSignupForm(values: SignupFormValues): SignupFieldErrors {
  const errors: SignupFieldErrors = {};
  const contactName = values.contactName.trim();
  const company = values.company.trim();
  const email = values.email.trim();
  const phone = values.phone.trim();

  if (contactName.length === 0 || contactName.length > 120) errors.contactName = 'validation.contactName';
  if (company.length === 0 || company.length > 200) errors.company = 'validation.company';
  if (!EMAIL_PATTERN.test(email) || email.length > 255) errors.email = 'validation.email';
  if (phone.length > 40 || !PHONE_PATTERN.test(phone)) errors.phone = 'validation.phone';
  if (!isValidBusinessId(values.businessId)) errors.businessId = 'validation.businessId';
  if (!isValidSlug(values.requestedSlug.trim())) errors.requestedSlug = 'validation.requestedSlug';
  if (!values.consent) errors.consent = 'validation.consent';
  return errors;
}

export interface SignupRequestPayload {
  contactName: string;
  company: string;
  email: string;
  businessId: string;
  requestedSlug: string;
  locale: 'fi' | 'en';
  consent: true;
  phone?: string;
  website?: string;
}

/** The page has FI and EN copy only, so the mail follows the language shown. */
export function signupLocaleFor(language: string): 'fi' | 'en' {
  return language.startsWith('en') ? 'en' : 'fi';
}

export function buildSignupPayload(
  values: SignupFormValues,
  locale: 'fi' | 'en',
  honeypot: string,
): SignupRequestPayload {
  const phone = values.phone.trim();
  return {
    contactName: values.contactName.trim(),
    company: values.company.trim(),
    email: values.email.trim(),
    businessId: values.businessId.trim(),
    requestedSlug: values.requestedSlug.trim(),
    locale,
    consent: true,
    ...(phone ? { phone } : {}),
    ...(honeypot ? { website: honeypot } : {}),
  };
}
